export interface OHLCVBar {
  time: number;     // UNIX timestamp (seconds)
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
  crossVolume?: number | null;
}

export interface OHLCVResponse {
  symbol: string;
  timeframe: Timeframe;
  count: number;
  data: OHLCVBar[];
}

export interface SymbolInfo {
  symbol: string;
  name: string;
  type: 'stock' | 'index';
  sector_id: number | null;
  logo?: string; // URL from company_profiles — populated once API exposes it
}

export interface SymbolsResponse {
  count: number;
  symbols: SymbolInfo[];
}

export interface SearchResult {
  symbol: string;
  name: string;
  type: 'stock' | 'index';
}

export interface SearchResponse {
  count: number;
  results: SearchResult[];
}

export type IntradayResolution = '1m' | '5m' | '15m' | '30m' | '1h';
export type Timeframe = '1D' | '1W' | '1M' | IntradayResolution;

export function isIntradayResolution(tf: Timeframe): tf is IntradayResolution {
  return tf === '1m' || tf === '5m' || tf === '15m' || tf === '30m' || tf === '1h';
}

/** Backend corporate-action categories the OHLCV series can be backward-adjusted for. */
export type PriceAdjustmentType = 'splits' | 'div' | 'div_script' | 'rights';

export interface IntradayResponse {
  symbol: string;
  resolution: IntradayResolution;
  count: number;
  data: OHLCVBar[];
}

export interface CrosshairData {
  time: number;
  open: number | null;
  high: number | null;
  low: number | null;
  close: number;
  volume: number | null;
}

export interface WatchlistEntry {
  symbol: string;
  name: string;
  type: 'stock' | 'index';
  logo?: string;
}

// ─── RS Lines ───────────────────────────────────────────────────────────────

export interface RSLineConfig {
  id: string;
  benchmarkSymbol: string;
  benchmarkName: string;
  color: string;
  visible: boolean;
  /** Extra downward pixel offset for this RS line (0–200 px) */
  offsetPx: number;
  /** Draw an EMA of the RS line with green/red fill between them */
  emaEnabled?: boolean;
  /** EMA period in bars (default 21) */
  emaPeriod?: number;
  /**
   * Optional: if set, this line compares stockSymbol vs benchmarkSymbol instead
   * of the currently charted stock vs benchmarkSymbol.
   */
  stockSymbol?: string;
  stockName?: string;
}

// ─── Scan-label event rules ───────────────────────────────────────────────────

/** One active event-label rule instance stored in chartStore / templates */
export interface ScanRuleConfig {
  ruleId:  string;
  enabled: boolean;
  params:  number[];
  color:   string;
}

// ─── Indicator Templates ──────────────────────────────────────────────────────

export interface IndicatorTemplate {
  id: string;
  name: string;
  indicators: Array<{ name: string; params?: number[]; colors?: string[] }>;
  candleType?: string;
  /** Scan-label configs to restore when this template is loaded */
  scanConfigs?: Record<string, ScanRuleConfig[]>;
  /** RS line configs to restore when this template is loaded */
  rsLineConfigs?: RSLineConfig[];
  /** Volume label toggle state */
  volumeLabelEnabled?: boolean;
  /** EPS Line (secondary axis) settings */
  epsLineEnabled?: boolean;
  epsLineAlignToAnnouncement?: boolean;
  epsLineAxisScale?: 'linear' | 'log';
  epsLineForwardModel?: 'ttm-yoy' | 'cagr-2y' | 'quarter-momentum';
  epsLineShowForwardProjection?: boolean;
  /** Foreign Holding sub-pane settings */
  foreignHoldingUpColor?: string;
  foreignHoldingDownColor?: string;
  foreignHoldingFlatColor?: string;
  foreignHoldingMA1Period?: number;
  foreignHoldingMA1Color?: string;
  foreignHoldingMA2Period?: number;
  foreignHoldingMA2Color?: string;
  foreignHoldingConfirmBullColor?: string;
  foreignHoldingDistributionColor?: string;
  foreignHoldingAccumulationColor?: string;
  foreignHoldingConfirmBearColor?: string;
  /** Cup & Handle pattern overlay settings */
  cupHandleEnabled?: boolean;
  cupHandleSettings?: CupHandleSettings;
  /** William O'Neil / IBD-style sell signal overlay settings */
  williamOneilSellEnabled?: boolean;
  williamOneilSellSettings?: WilliamOneilSellSettings;
  /** Chart visual settings (colors, grid, marks, crosshair, axis) */
  chartSettings?: ChartSettingsSnapshot;
  /** For guest local templates — marks the default template */
  isDefault?: boolean;
}

// ─── Cup & Handle Pattern Indicator ────────────────────────────────────────────

/** Configurable detection parameters for the CUP_HANDLE overlay indicator. */
export interface CupHandleSettings {
  /** Pivot high/low lookback+lookahead, in bars */
  pivotBars: number;
  /** Rolling window used to find the base/cup rim, in bars */
  rimWindowBars: number;
  /** Minimum prior leg-up bars scanned for the base's feeding advance */
  priorLegBars: number;
  /** Minimum required leg-up size before the rim, as a fraction (e.g. 0.20 = 20%) */
  minLegUpPct: number;
  /** Cup must complete within this many bars */
  minLengthBars: number;
  maxLengthBars: number;
  /** Maximum cup depth below the rim, as a fraction (e.g. 0.5 = 50%) */
  maxDepthPct: number;
  /** How close (fraction of rim price) the right side must climb to trigger handle tracking */
  handleTriggerPct: number;
  /** Maximum handle pullback depth, as a fraction of the cup's total depth */
  maxHandleDepthPct: number;
  minHandleBars: number;
  maxHandleBars: number;
  /** Hide the pattern entirely below this confidence score (0-100) */
  minConfidenceToShow: number;
}

// ─── William O'Neil / IBD Sell Signal Overlay ─────────────────────────────────

/** Break below a key moving average (50-day style) on above-average volume. */
export interface Ma50BreakRuleSettings {
  enabled: boolean;
  /** Moving-average period, in bars */
  maPeriod: number;
  /** Volume must exceed this multiple of the rolling average volume */
  volMultiplier: number;
  /** Rolling window used to compute average volume, in bars */
  avgVolPeriod: number;
}

/** Break below the long-term (200-day style) moving average. */
export interface Ma200BreakRuleSettings {
  enabled: boolean;
  maPeriod: number;
}

/** Climax top: largest single-bar % gain of the advance on the heaviest volume of the advance. */
export interface ClimaxTopRuleSettings {
  enabled: boolean;
  /** Trailing window scanned for the "move", in bars */
  lookbackBars: number;
  /** Minimum prior advance (from the window's low) required to call it an extended run */
  minAdvancePct: number;
  /** Volume must exceed this multiple of the rolling average volume */
  volMultiplier: number;
}

/** Exhaustion gap: a large gap-up on heavy volume after an extended advance. */
export interface ExhaustionGapRuleSettings {
  enabled: boolean;
  /** Minimum opening gap, as a fraction of the prior close */
  minGapPct: number;
  /** Minimum prior advance required, over `advanceLookbackBars` */
  minAdvancePct: number;
  advanceLookbackBars: number;
  volMultiplier: number;
}

/** Distribution days: heavy-volume down days clustering within a trailing window. */
export interface DistributionDaysRuleSettings {
  enabled: boolean;
  /** Trailing window, in bars, that distribution days are counted within */
  windowBars: number;
  /** Minimum decline to count as a "down day", as a fraction */
  minDownPct: number;
  /** A down day counts as distribution when its volume exceeds this multiple of the prior day's volume */
  volMultiplier: number;
  /** Number of distribution days within the window that triggers the threshold event */
  thresholdCount: number;
}

/** Pivot stop: 7-8%-style stop-loss measured from the most recent breakout pivot, not a manual cost basis. */
export interface PivotStopRuleSettings {
  enabled: boolean;
  /** Stop distance below the pivot, as a fraction (e.g. 0.075 = 7.5%) */
  stopPct: number;
  /** How far back to look for a qualifying, since-confirmed pivot high, in bars */
  pivotLookbackBars: number;
  /** Pivot high lookback+lookahead, in bars */
  pivotBars: number;
}

export interface WilliamOneilSellSettings {
  ma50Break: Ma50BreakRuleSettings;
  ma200Break: Ma200BreakRuleSettings;
  climaxTop: ClimaxTopRuleSettings;
  exhaustionGap: ExhaustionGapRuleSettings;
  distributionDays: DistributionDaysRuleSettings;
  pivotStop: PivotStopRuleSettings;
}

/** All chart visual settings saved/restored per template */
export interface ChartSettingsSnapshot {
  candleType?: string;
  candleUpColor?: string;
  candleDownColor?: string;
  hlcUpColor?: string;
  hlcDownColor?: string;
  showHGrid?: boolean;
  showVGrid?: boolean;
  showPriceMarkHigh?: boolean;
  showPriceMarkLow?: boolean;
  showLastPriceLine?: boolean;
  showCrosshair?: boolean;
  yAxisReverse?: boolean;
  priceScaleType?: string;
}

// ─── Auth ─────────────────────────────────────────────────────────────────────

export interface User {
  id: string;
  email: string;
  name: string | null;
  avatarUrl: string | null;
  emailVerified: boolean;
  plan: 'basic' | 'pro' | 'business';
  planExpiresAt: string | null;
  trialEndsAt: string | null;
  trialUsed: boolean;
  telegramChatId: string | null;
  pendingEmail: string | null;
  isGoogleAccount: boolean;
  marketingOptIn: boolean;
}

export interface ServerWatchlist {
  id: string;
  name: string;
  symbols: WatchlistEntry[];
  is_default: number;
  created_at: string;
  updated_at: string;
}

/** A user-drawn klinecharts overlay, reduced to the fields needed to re-create it. */
export interface SerializedOverlay {
  name: string;
  /** Pane the drawing was on. Sub-pane ids are generated per session, so for
   *  those `paneIndicator` (the owning indicator's name) is the stable key. */
  paneId: string;
  paneIndicator?: string;
  /** `dataIndex` is only kept when a point has no timestamp (drawn past the last bar). */
  points: Array<{ timestamp?: number; dataIndex?: number; value?: number }>;
  mode?: string;
  lock?: boolean;
  visible?: boolean;
  styles?: Record<string, unknown> | null;
  extendData?: unknown;
}

export interface IndicatorTemplateData {
  version: 1;
  indicators: Array<{ name: string; params?: number[]; colors?: string[] }>;
  /** Scan-label configs to restore when this template is applied */
  scanConfigs?: Record<string, ScanRuleConfig[]>;
  /** RS line configs to restore when this template is applied */
  rsLineConfigs?: RSLineConfig[];
  /** Volume label toggle state */
  volumeLabelEnabled?: boolean;
  /** EPS Line (secondary axis) settings */
  epsLineEnabled?: boolean;
  epsLineAlignToAnnouncement?: boolean;
  epsLineAxisScale?: 'linear' | 'log';
  epsLineForwardModel?: 'ttm-yoy' | 'cagr-2y' | 'quarter-momentum';
  epsLineShowForwardProjection?: boolean;
  /** Foreign Holding sub-pane settings */
  foreignHoldingUpColor?: string;
  foreignHoldingDownColor?: string;
  foreignHoldingFlatColor?: string;
  foreignHoldingMA1Period?: number;
  foreignHoldingMA1Color?: string;
  foreignHoldingMA2Period?: number;
  foreignHoldingMA2Color?: string;
  foreignHoldingConfirmBullColor?: string;
  foreignHoldingDistributionColor?: string;
  foreignHoldingAccumulationColor?: string;
  foreignHoldingConfirmBearColor?: string;
  /** Cup & Handle pattern overlay settings */
  cupHandleEnabled?: boolean;
  cupHandleSettings?: CupHandleSettings;
  /** William O'Neil / IBD-style sell signal overlay settings */
  williamOneilSellEnabled?: boolean;
  williamOneilSellSettings?: WilliamOneilSellSettings;
  chartSettings?: ChartSettingsSnapshot;
  /** Manual chart drawings — only populated for shared links, not saved templates */
  drawings?: SerializedOverlay[];
}

export interface ServerTemplate {
  id: string;
  name: string;
  template: IndicatorTemplateData;
  is_default: number;
  created_at: string;
  updated_at: string;
}

// ─── Screener ─────────────────────────────────────────────────────────────────

export interface ScreenerRow {
  symbol: string;
  name: string;
  close: number;
  change_pct: number | null;
  volume: number | null;
  vol_sma10: number | null;
  vol_sma20: number | null;
  vol_sma50: number | null;
  vol_vs_sma50_pct: number | null;
  is_hve: number;
  is_hv1: number;
  is_hvytd: number;
  sma10: number | null;
  ema21: number | null;
  ema50: number | null;
  ema200: number | null;
  above_sma10: number;
  above_ema21: number;
  above_ema50: number;
  above_ema200: number;
  high_52w: number | null;
  low_52w: number | null;
  is_52w_high: number;
  is_52w_low: number;
  rs_line: number | null;
  rs_rating: number;
  rsi14: number | null;
}

export interface ScreenerFilterResponse {
  date: string | null;
  count: number;
  data: ScreenerRow[];
}

export interface ScreenerCountsRow {
  trade_date: string;
  count_above_sma10: number;
  count_above_ema21: number;
  count_above_ema50: number;
  count_above_ema200: number;
  count_52w_high: number;
  count_52w_low: number;
  total_stocks: number;
  aspi_close: number;
}

export interface ScreenerCountsResponse {
  count: number;
  data: ScreenerCountsRow[];
}

export interface SeasonalMonth {
  month: string;           // "01"–"12"
  avg_daily_return_pct: number;
  trading_days: number;
}

export interface ScreenerSeasonalResponse {
  years: number;
  start_date: string;
  data: SeasonalMonth[];
}

export type ScreenerSeasonalGranularity = 'monthly' | 'weekly' | 'daily';

export interface SeasonalPeriod {
  period: string;
  avg_daily_return_pct: number;
  trading_days: number;
}

export interface ScreenerSeasonalV2Response {
  years: number;
  start_date: string;
  granularity: ScreenerSeasonalGranularity;
  data: SeasonalPeriod[];
}

export interface ScreenerRsHistoryResponse {
  count: number;
  data: Record<string, number[]>;
}

export interface Quote {
  symbol: string;
  price:  number | null;
  chg:    number | null;
  chgPct: number | null;
}

export interface QuotesResponse {
  data: Quote[];
}

export interface ScreenerSeasonalYearsResponse {
  granularity: ScreenerSeasonalGranularity;
  data: Record<string, Array<{ period: string; indexed: number }>>;
}

export interface ScreenerRatingResponse {
  symbol: string;
  rs_rating: number | null;
  trade_date: string | null;
}

export interface ScreenerIndexItem {
  id: number;
  symbol: string;
  name: string;
}

export interface ScreenerIndicesResponse {
  count: number;
  data: ScreenerIndexItem[];
}

export interface WorldIndexItem {
  market: string;
  name: string;
  symbol: string;
  provider: 'yahoo-chart';
  resolvedTicker: string | null;
  value: number | null;
  dayReturnPct: number | null;
  weekReturnPct: number | null;
  asOf: string | null;
  currency: string | null;
  timezone: string | null;
  status: 'ok' | 'error';
  error: string | null;
}

export interface WorldIndicesResponse {
  fetchedAt: string;
  count: number;
  okCount: number;
  provider: 'yahoo-chart';
  data: WorldIndexItem[];
}

export interface CommodityItem {
  label: string;
  symbol: string;
  provider: 'yahoo-chart';
  resolvedTicker: string | null;
  value: number | null;
  dayReturnPct: number | null;
  weekReturnPct: number | null;
  asOf: string | null;
  currency: string | null;
  timezone: string | null;
  status: 'ok' | 'error';
  error: string | null;
}

export interface CommoditiesResponse {
  fetchedAt: string;
  count: number;
  okCount: number;
  provider: 'yahoo-chart';
  data: CommodityItem[];
}

export interface ScreenerRelPerfResponse {
  date: string | null;
  count: number;
  data: ScreenerRow[];
}

// ─── Corporate Events ────────────────────────────────────────────────────────

export type CorporateEventType =
  | 'div'           // Cash/special/interim dividend — XD date
  | 'div_script'    // Scrip/bonus dividend — XD date
  | 'div_ann'       // Dividend announcement — announced_date
  | 'rights'        // Rights issue — XR date
  | 'rights_ann'    // Rights announcement — announced_date
  | 'split'         // Share subdivision — ex_date
  | 'split_ann'     // Share subdivision announcement — announced_date
  | 'consolidation' // Share consolidation — ex_date
  | 'consolidation_ann' // Share consolidation announcement — announced_date
  | 'report'        // Financial report — announced_date
  | 'agm'           // Annual General Meeting
  | 'egm'           // Extraordinary General Meeting
  | 'halt';         // Trading halt/suspension

export interface CorporateEvent {
  /** YYYY-MM-DD primary chart date */
  date: string;
  type: CorporateEventType;
  title: string;
  detail: string;
  pdfUrl: string | null;
  announcedDate: string | null;
  xdDate: string | null;
  ratio: string | null;
  amount: number | null;
}

export interface EventsResponse {
  symbol: string;
  count: number;
  events: CorporateEvent[];
}

export interface ForeignHoldingPoint {
  /** YYYY-MM-DD report date */
  date: string;
  /** foreign_holding / qty_cds * 100, null if unavailable or outside [0, 100] (source data sanity guard) */
  pct: number | null;
  /** Raw shares held by foreign investors */
  foreignHolding: number;
  /** Total CDS-deposited shares (ownership denominator), null if unavailable */
  qtyCds: number | null;
}

export interface ForeignHoldingResponse {
  symbol: string;
  series: ForeignHoldingPoint[];
}

export interface ScreenerFilterParams {
  date?: string;
  above_sma10?: boolean;
  above_ema21?: boolean;
  above_ema50?: boolean;
  above_ema200?: boolean;
  is_52w_high?: boolean;
  is_52w_low?: boolean;
  is_hve?: boolean;
  is_hv1?: boolean;
  is_hvytd?: boolean;
  vol_vs_sma50_min?: number;
  vol_vs_sma50_max?: number;
  change_pct_min?: number;
  change_pct_max?: number;
  rs_rating_min?: number;
  rs_rating_max?: number;
  rsi14_min?: number;
  rsi14_max?: number;
  sort?: string;
  order?: 'asc' | 'desc';
  limit?: number;
}

// ─── Home page ───────────────────────────────────────────────────────────────
// Mirrors HomeResponse in workers/ohlcv-bff/src/handlers/home.ts.

export interface HomeStockRow {
  symbol: string;
  name: string;
  close: number;
  change_pct: number;
}

export interface HomeLeaderRow {
  symbol: string;
  name: string;
  close: number;
  change_pct: number | null;
  rs_rating: number;
}

export interface HomeIndexRow {
  symbol: string;
  name: string;
  close: number;
  change_pct: number;
}

export interface HomeHeadlineRow {
  symbol: string;
  label: string;
  close: number;
  change_pct: number | null;
}

export interface HomeBreadth {
  advancers: number;
  decliners: number;
  unchanged: number;
  total: number;
  above_ema50: number;
  count_52w_high: number;
  count_52w_low: number;
}

export interface HomeResponse {
  date: string | null;
  volumeFloor: number;
  stockCount: number;
  eligibleCount: number;
  headline: HomeHeadlineRow[];
  gainers: HomeStockRow[];
  losers: HomeStockRow[];
  sectorIndices: HomeIndexRow[];
  leaders: HomeLeaderRow[];
  breadth: HomeBreadth | null;
}

// ─── Market Health Index (MHI worker, via /api/mhi/*) ────────────────────────

export type MhiPhase = 'Bear/Crash' | 'Bearish' | 'Neutral' | 'Bullish' | 'Bull Run';

export interface MhiLatest {
  date: string;
  close: number;
  mhi: number;
  phase: MhiPhase;
  vol: number;
  divergence: string;
  price_pct: number;
}

/** One day of the ASI/MHI series. `m` is null during the indicator's warm-up (before 24 May 2010). */
export interface MhiHistoryRow {
  d: string;
  c: number;
  m: number | null;
  p: MhiPhase;
  v: number;
}
